import type { WidgetRenderContext } from "@salestrak/ui";

import { FollowUpEndMenuItem } from "#app/components/follow-ups/follow-up-section";
import { useIssueViewCapabilities } from "#app/components/issue-view/issue-view-capabilities";
import { useProjectCycleMutations } from "#app/hooks/issue-view/use-project-cycle-mutations";
import { useWorkspaceData } from "#app/lib/data/workspace-data-provider";
import { usePermission } from "#app/lib/hooks/use-permission";
import { effectiveProjectCycle } from "#app/lib/project-updates";
import { useIssueWidgetContext } from "#app/widgets/issue/issue-widget-context";
import type { IssueUpdatesConfig } from "./index";

/** The section menu's End Follow up, the same row an Account section carries.
 *  On a Project it asks nothing — stopping tracking is a preference, not a
 *  business outcome — and the toast offers Undo instead. Ending one Project's
 *  Follow up rides `manage.project_updates_off` (Admins by default, grantable
 *  to Members); a viewer without it sees the row disabled with the reason. */
export function IssueUpdatesMenuItems(_: WidgetRenderContext<IssueUpdatesConfig>) {
  const ctx = useIssueWidgetContext();
  const { canEdit } = useIssueViewCapabilities();
  const canTurnOff = usePermission("manage.project_updates_off");
  const { stageMap, workflowMap } = useWorkspaceData();
  const issue = ctx.issue;
  const { endFollowUp } = useProjectCycleMutations(issue?.issueId ?? "");
  if (!issue || !canEdit) return null;
  const stage = ctx.stage ?? stageMap.get(issue.stageId) ?? null;
  const effective = effectiveProjectCycle(
    issue.age,
    stage,
    workflowMap.get(issue.workflowId) ?? null,
  );
  if (!effective.cycle) return null;
  const own = effective.source === "project" ? effective.cycle : null;
  return (
    <FollowUpEndMenuItem
      onEnd={() => void endFollowUp(own)}
      {...(canTurnOff
        ? {}
        : { disabledReason: "You don’t have permission to end this Project’s Follow up" })}
    />
  );
}
